import React from "react";

import moment from "moment-timezone";

import { Box, Typography, Divider, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { FarmRelationshipEnum, Trip } from "../../types";
import {
  grayMedium,
  grayDark,
  alabaster,
  errorRed,
  tintRed,
  black,
  white,
  moovsBlue,
} from "../../design-system/colors";
import PricingItemsBlock from "./PricingItemsBlock";
import RoundChip from "../../design-system/components/RoundChip";
import { useScreenSize, useOperatorCurrency, useTimeFormat, useOperator } from "../../globals/hooks";

export type PricingBlockProps = {
  trip: Trip;
  requestOrderNo: string;
};

const externalRefOverlineSx = {
  color: grayDark,
  display: "block",
  mt: 0.5,
};

const desktopColumnSx = {
  minWidth: 0,
  flexShrink: 0,
};

function PricingBlock(props: PricingBlockProps) {
  const { isMobileView } = useScreenSize();
  const { trip, requestOrderNo } = props;
  const currency = useOperatorCurrency();
  const { formatDateTime } = useTimeFormat();
  const { operator } = useOperator();
  const pricingLayout = operator?.settings?.pricingLayout;

  const confNo = trip.tripNumber
    ? `${requestOrderNo}-${trip.tripNumber}`
    : requestOrderNo;

  const passengerName = trip.tempPassenger?.name
    ? `${trip.tempPassenger.name}`
    : `${trip.contact.firstName} ${trip.contact.lastName}`;

  const { farmRelationship, isFarmedRoute } = trip.routes[0];

  const isFarmee =
    isFarmedRoute && farmRelationship === FarmRelationshipEnum.Farmee;

  const isCancelledTrip = !!trip.cancelledAt;
  const hasCancellationFee =
    isCancelledTrip && !!trip.routes[0].pricing.cancellationFee;
  const shouldShowPricingBlock =
    !isCancelledTrip || (isCancelledTrip && hasCancellationFee);

  // render methods
  const renderCancelledChip = () => (
    <RoundChip label="CANCELLED" color={errorRed} backgroundColor={tintRed} />
  );

  const pickUpAddress = trip.stops[0].location;

  const renderMetadataField = (label: string, value: React.ReactNode) => (
    <Box minWidth={0}>
      <Typography variant="overline" sx={{ color: grayDark }}>
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );

  // Format all stop addresses as a numbered list
  const formatAllStopAddresses = () => {
    return (
      <Box sx={{ p: 1 }}>
        {trip.stops.map((stop, index) => {
          let stopLabel;
          if (index === 0) {
            stopLabel = "Pick-up";
          } else if (index === trip.stops.length - 1) {
            stopLabel = "Drop-off";
          } else {
            stopLabel = `Stop ${index}`;
          }

          return (
            <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
              <Box
                component="span"
                sx={{ color: moovsBlue, fontWeight: "bold" }}
              >
                {stopLabel}
              </Box>
              {`: ${stop.location}`}
            </Typography>
          );
        })}
      </Box>
    );
  };

  const renderPickupAddressField = () => (
    <Tooltip
      title={formatAllStopAddresses()}
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: white,
            color: black,
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
            maxWidth: "1000px",
            width: "100%",
          },
        },
      }}
    >
      <Box
        display="flex"
        flex={isMobileView ? "1" : "1 1 0"}
        flexDirection="column"
        mb={isMobileView ? 1 : 0}
        mr={isMobileView ? 2 : 0}
        width={isMobileView ? "100%" : undefined}
        sx={{
          minWidth: 0,
          cursor: "pointer",
          position: "relative",
          borderRadius: "4px",
          transition: "all 0.2s ease",
        }}
      >
        <Typography variant="overline" sx={{ color: grayDark }}>
          pick-up address
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" sx={{ mr: 1 }}>
            {pickUpAddress}
          </Typography>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{
              color: moovsBlue,
              opacity: 0.8,
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );

  const renderPricingBlock = () => (
    <>
      <Box
        display="flex"
        flex="1"
        flexDirection="row"
        mb={1}
        px={isMobileView ? 0 : 2}
      >
        <Box display="flex" flex="1.5">
          <Typography variant="overline" sx={{ color: grayDark }}>
            item
          </Typography>
        </Box>
        <Box display="flex" flex=".5" justifyContent="flex-end">
          <Typography variant="overline" sx={{ color: grayDark }}>
            amount
          </Typography>
        </Box>
      </Box>
      <Box
        bgcolor={isMobileView ? "" : alabaster}
        pt={2}
        px={isMobileView ? 0 : 2}
      >
        <PricingItemsBlock
          pricing={
            isFarmee ? trip.routes[0].farmeePricing : trip.routes[0].pricing
          }
          pricingLayout={pricingLayout}
        />
      </Box>
      {isMobileView && <Divider />}
      <Box
        display="flex"
        flex="1"
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="baseline"
        mt={isMobileView ? 2 : 4}
      >
        <Box mr={4}>
          <Typography variant="h6" sx={!isMobileView && { fontWeight: "bold" }}>
            Subtotal
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {currency(trip.totalAmount)}
        </Typography>
      </Box>
    </>
  );

  return (
    <Box
      border={1}
      borderColor={grayMedium}
      borderRadius="4px"
      display="flex"
      flex="1"
      flexDirection="column"
      py={2}
      px={isMobileView ? 2 : 3}
    >
      {!isMobileView ? (
        <Box>
          <Box
            display="grid"
            gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            columnGap={6}
          >
            {renderMetadataField("conf no", confNo)}
            {renderMetadataField("passenger name", passengerName)}
            {renderMetadataField("vehicle", trip.routes[0].vehicle?.name)}
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            columnGap={6}
            mt={2}
          >
            {renderMetadataField(
              "trip date & time",
              formatDateTime(moment.utc(trip.stops[0].dateTime), true)
            )}
            {renderPickupAddressField()}
            {trip.externalReferenceNumber ? (
              renderMetadataField(
                "external ref no",
                trip.externalReferenceNumber
              )
            ) : (
              <Box />
            )}
          </Box>
          {isCancelledTrip && (
            <Box mt={2}>{renderCancelledChip()}</Box>
          )}
        </Box>
      ) : (
        <Box
          display="flex"
          flex="1"
          flexDirection={isMobileView ? "column" : "row"}
          alignItems={isMobileView ? "stretch" : "flex-start"}
        >
          <Box
            display="flex"
            mr={isMobileView ? 2 : 4}
            width={isMobileView ? "100%" : undefined}
            sx={
              !isMobileView
                ? { ...desktopColumnSx, flexBasis: 130 }
                : undefined
            }
          >
            <Box
              display="flex"
              flex="1"
              flexDirection="column"
              mb={isMobileView ? 1 : 0}
            >
              <Typography variant="overline" sx={{ color: grayDark }}>
                conf no
              </Typography>
              <Typography variant="h6">{confNo}</Typography>
              {trip.externalReferenceNumber && (
                <>
                  <Typography variant="overline" sx={externalRefOverlineSx}>
                    external ref no
                  </Typography>
                  <Typography variant="h6">
                    {trip.externalReferenceNumber}
                  </Typography>
                </>
              )}
            </Box>
            {isMobileView && isCancelledTrip && renderCancelledChip()}
          </Box>
          <Box
            display="flex"
            mr={isMobileView ? 2 : 4}
            width={isMobileView ? "100%" : undefined}
            sx={
              !isMobileView
                ? { ...desktopColumnSx, flexBasis: 150 }
                : undefined
            }
          >
            <Box
              display="flex"
              flex="1"
              flexDirection="column"
              mb={isMobileView ? 1 : 0}
            >
              <Typography variant="overline" sx={{ color: grayDark }}>
                passenger name
              </Typography>
              <Typography variant="h6">{passengerName}</Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            mb={isMobileView ? 1 : 0}
            mr={isMobileView ? 2 : 4}
            width={isMobileView ? "100%" : undefined}
            sx={
              !isMobileView
                ? { ...desktopColumnSx, flexBasis: 220 }
                : undefined
            }
          >
            <Typography variant="overline" sx={{ color: grayDark }}>
              vehicle
            </Typography>
            <Typography variant="h6">{trip.routes[0].vehicle?.name}</Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            mb={isMobileView ? 1 : 0}
            mr={isMobileView ? 1 : 4}
            width={isMobileView ? "100%" : undefined}
            sx={
              !isMobileView
                ? { ...desktopColumnSx, flexBasis: 220 }
                : undefined
            }
          >
            <Typography variant="overline" sx={{ color: grayDark }}>
              trip date & time
            </Typography>
            <Typography variant="h6">
              {formatDateTime(moment.utc(trip.stops[0].dateTime), true)}
            </Typography>
          </Box>
          {renderPickupAddressField()}
          {!isMobileView && isCancelledTrip && renderCancelledChip()}
        </Box>
      )}

      {shouldShowPricingBlock && (
        <>
          <Box my={2}>
            <Divider />
          </Box>
          {renderPricingBlock()}
        </>
      )}
    </Box>
  );
}

export default PricingBlock;
